import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AddMcpServer, RemoveMcpServer } from "../../../schemas/codegen";
import { projectWebSocketManager } from "../websocket/ProjectWebSocketManager";
import { QUERY_KEYS } from "./queryKeys";

export interface AddMcpServerParams {
  projectId: string;
  url: string;
  name: string;
  bearerToken?: string;
}

export interface RemoveMcpServerParams {
  projectId: string;
  serverId: string;
}

export function useAddMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      url,
      name,
      bearerToken,
    }: AddMcpServerParams): Promise<boolean> => {
      const connection = projectWebSocketManager.getConnection(projectId);

      const addCommand: AddMcpServer = {
        type: "mcp.server.add",
        url,
        name,
        bearerToken,
      };

      // Ensure connection is established
      if (!connection.isConnected()) {
        try {
          await connection.connect();
        } catch (error) {
          console.error(
            `Failed to connect to project ${projectId} for MCP server addition:`,
            error,
          );
          throw new Error("Failed to connect to project");
        }
      }

      // Send add command via websocket
      const success = connection.send(addCommand);
      if (!success) {
        throw new Error("Failed to send add MCP server command");
      }
      return success;
    },
    onSuccess: (_, { projectId }) => {
      // Invalidate and refetch the MCP servers query
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MCP_SERVERS, projectId],
      });
    },
  });
}

export function useRemoveMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      serverId,
    }: RemoveMcpServerParams): Promise<boolean> => {
      const connection = projectWebSocketManager.getConnection(projectId);

      const removeCommand: RemoveMcpServer = {
        type: "mcp.server.remove",
        serverId,
      };

      // Ensure connection is established
      if (!connection.isConnected()) {
        try {
          await connection.connect();
        } catch (error) {
          console.error(
            `Failed to connect to project ${projectId} for MCP server removal:`,
            error,
          );
          throw new Error("Failed to connect to project");
        }
      }

      // Send remove command via websocket
      const success = connection.send(removeCommand);
      if (!success) {
        throw new Error("Failed to send remove MCP server command");
      }
      return success;
    },
    onSuccess: (_, { projectId }) => {
      // Invalidate and refetch the MCP servers query
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MCP_SERVERS, projectId],
      });
    },
  });
}
